/**
 * Google reCAPTCHA v3 loader.
 *
 * The script is fetched only when a site key exists, so a deployment without
 * reCAPTCHA configured makes no request to Google at all — which matters on a
 * page this confidential.
 *
 * Tokens expire after about two minutes, so one is minted at submit time
 * rather than on page load.
 */

declare global {
  interface Window {
    grecaptcha?: {
      ready(cb: () => void): void;
      execute(siteKey: string, opts: { action: string }): Promise<string>;
    };
  }
}

const SCRIPT_ID = 'recaptcha-v3';

/** Thrown when the script cannot be reached — usually a blocker or a firewall. */
export class RecaptchaUnavailableError extends Error {
  constructor() {
    super('recaptcha_unavailable');
    this.name = 'RecaptchaUnavailableError';
  }
}

let loading: Promise<void> | null = null;

function injectScript(siteKey: string): Promise<void> {
  return new Promise((resolve, reject) => {
    const existing = document.getElementById(SCRIPT_ID);
    if (existing) {
      resolve();
      return;
    }
    const s = document.createElement('script');
    s.id = SCRIPT_ID;
    s.src = `https://www.google.com/recaptcha/api.js?render=${encodeURIComponent(siteKey)}`;
    s.async = true;
    s.defer = true;
    s.onload = () => resolve();
    s.onerror = () => {
      s.remove();
      reject(new RecaptchaUnavailableError());
    };
    document.head.appendChild(s);

    // onerror does not fire for every kind of block, so bound the wait.
    window.setTimeout(() => {
      if (!window.grecaptcha) {
        reject(new RecaptchaUnavailableError());
      }
    }, 10_000);
  });
}

export function loadRecaptcha(siteKey: string): Promise<void> {
  if (!siteKey) return Promise.resolve();
  if (!loading) {
    loading = injectScript(siteKey).catch((err) => {
      // Allow a later attempt to retry rather than caching the failure forever.
      loading = null;
      throw err;
    });
  }
  return loading;
}

/**
 * Mint a token for one submission. Resolves to '' when reCAPTCHA is switched
 * off; throws RecaptchaUnavailableError when it is on but unreachable, so the
 * caller can say something useful instead of failing silently.
 */
export async function getRecaptchaToken(siteKey: string, action: string): Promise<string> {
  if (!siteKey) return '';
  await loadRecaptcha(siteKey);

  const g = window.grecaptcha;
  if (!g) throw new RecaptchaUnavailableError();

  await new Promise<void>((resolve) => g.ready(resolve));
  try {
    return await g.execute(siteKey, { action });
  } catch {
    throw new RecaptchaUnavailableError();
  }
}
